import { useMemo, useState } from "react";

import clsx from "clsx";
import { cloneDeep } from "lodash";
import { v4 as uuidv4 } from "uuid";

import { GenericTooltip } from "@components/tooltip-container";
import {
  ButtonGroup,
  LoadingIndicator,
  PrimaryIconButton,
  SecondaryIconButton,
} from "@components/v2";
import { usePrettyLogEventPage } from "@components/v2/analytics/hooks";
import { Switch } from "@components/v2/switch";
import { TileModal } from "@features/metrics-explorer/components";
import { Chart } from "@features/metrics-explorer/components/metrics/chart";
import { useMetricsExplorerContext } from "@features/metrics-explorer/hooks";

import { STACKED_CHART_SUPPORTED_METRICS } from "../constants";
import { useSalesV4Context } from "../hooks";
import { isStackedChartAvailable } from "../utils";

import { EmptyState } from "./empty-state";
import { IconAggregate } from "./icon-aggregate";
import { IconSplit } from "./icon-split";
import { Legend } from "./legend";
import { MiniMetricCard } from "./mini-metric-card";

interface Props {
  onOpenMetricsExplorer: () => void;
}

export const SalesChart = ({ onOpenMetricsExplorer }: Props) => {
  const { tableStates, chartState, metrics, setChartState } =
    useSalesV4Context();
  const { tiles, metricsData, indexData, setTiles } =
    useMetricsExplorerContext();
  const prettyLogEventPage = usePrettyLogEventPage();

  const [isTileModalOpen, setIsTileModalOpen] = useState(false);

  const isLoading = useMemo(() => {
    const isMetricsDataLoading = metricsData.some(
      (metricData) => metricData.status === "loading",
    );
    const isIndexDataLoading = indexData.some(
      (datum) => datum.status === "loading",
    );

    return isMetricsDataLoading || isIndexDataLoading;
  }, [metricsData, indexData]);

  const supportedMetricNames = useMemo(() => {
    return metrics
      .filter((e) => STACKED_CHART_SUPPORTED_METRICS.includes(e.id))
      .map((e) => e.name);
  }, [metrics]);

  const isStackedOptionDisabled = !isStackedChartAvailable(chartState.metrics);

  return (
    <div
      className={clsx(
        "border border-v2-tertiary rounded-lg p-4 text-v2-primary bg-v2-primary h-[560px]",
        "flex flex-col",
      )}
    >
      <div className="flex items-start justify-between gap-2 mb-4">
        <div className="flex items-center gap-1 shrink-0">
          <PrimaryIconButton
            size="sm"
            className="w-auto pl-1 pr-2"
            onClick={() => {
              onOpenMetricsExplorer();
              prettyLogEventPage({
                domain: "Sales Attribution",
                action: "onClick",
                additionalActionInfo: "Open Metrics Explorer",
                data: {
                  pageContext: {
                    ...chartState,
                  },
                },
              });
            }}
          >
            <div className="h-[24px] w-[24px] bg-v2-overlay-shadow-200 rounded-[24px] flex items-center justify-center">
              <i className="fa-regular fa-arrow-left-to-line" />
            </div>
            <i className="fa-regular fa-telescope text-white" />
          </PrimaryIconButton>
        </div>
        <div className="flex-1 flex items-center flex-wrap gap-2">
          <div className="flex items-center flex-wrap gap-1">
            {chartState.metrics.map((e) => (
              <MiniMetricCard key={e.id} tile={e} />
            ))}
          </div>
          <SecondaryIconButton
            size="sm"
            className="border-v2-tertiary text-v2-secondary"
            onClick={() => setIsTileModalOpen(true)}
          >
            <i className="fa-regular fa-plus" />
          </SecondaryIconButton>
        </div>
        <div className="shrink-0">
          <GenericTooltip
            noInfoCircle
            mouseEnterDelay={500}
            content="Align Axis"
            placement="top"
          >
            <div
              className={clsx(
                "w-8 h-8 border border-v2-tertiary rounded-md flex items-center justify-center",
                "hover:bg-v2-highlight-information hover:border-v2-light",
              )}
            >
              <Switch
                size="x-small"
                value={chartState.alignAxesScale}
                onChange={(val) =>
                  setChartState({ ...chartState, alignAxesScale: val })
                }
              />
            </div>
          </GenericTooltip>
        </div>
        <ButtonGroup
          mode="expandable"
          variant="light"
          options={[
            {
              className: "p-0",
              value: "split",
              label: (
                <GenericTooltip
                  className="flex items-center justify-center w-8 h-full"
                  content="Split"
                  noInfoCircle
                  mouseEnterDelay={500}
                  placement="top"
                >
                  <IconSplit />
                </GenericTooltip>
              ),
            },
            {
              className: "p-0",
              value: "aggregated",
              label: (
                <GenericTooltip
                  className="flex items-center justify-center w-8 h-full"
                  content="Aggregate"
                  noInfoCircle
                  mouseEnterDelay={500}
                  placement="top"
                >
                  <IconAggregate />
                </GenericTooltip>
              ),
            },
            {
              className: "p-0",
              disabled: isStackedOptionDisabled,
              value: "stacked",
              label: (
                <GenericTooltip
                  className="flex items-center justify-center w-8 h-full"
                  content={
                    isStackedOptionDisabled ? (
                      <div>
                        <p>
                          Stacked chart is available only when first metrics is
                          one of:
                        </p>
                        <ul className="list-disc pl-4">
                          {supportedMetricNames.map((e) => (
                            <li key={e}>{e}</li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      "Stacked"
                    )
                  }
                  noInfoCircle
                  mouseEnterDelay={500}
                  placement="top"
                >
                  <i className="fa-light fa-chart-area"></i>
                </GenericTooltip>
              ),
            },
          ]}
          onChange={(value) => {
            setChartState({ ...chartState, displayMode: value });
          }}
          value={chartState.displayMode}
        />
      </div>
      <div className="relative w-full flex-1 overflow-hidden">
        {isLoading && (
          <div className="absolute left-[50%] top-[50%] translate-x-[-26px] translate-y-[-26px]">
            <LoadingIndicator />
          </div>
        )}
        {tiles.length > 0 ? (
          <Chart />
        ) : (
          <EmptyState onClick={() => setIsTileModalOpen(true)} />
        )}
      </div>
      {tiles.length > 0 && (
        <Legend
          tiles={tiles}
          onChange={(id) => {
            setTiles(
              tiles.map((e) =>
                e.id === id ? { ...e, visible: !e.visible } : e,
              ),
            );
          }}
        />
      )}
      {isTileModalOpen && (
        <TileModal
          isOpen={isTileModalOpen}
          initialFilters={cloneDeep(tableStates[0].breakdownFilters)}
          onClose={() => {
            setIsTileModalOpen(false);
          }}
          onSubmit={(value) => {
            const newTiles = [
              ...chartState.metrics,
              {
                id: uuidv4(),
                name: value.name,
                metricId: value.metricId,
                attributionModel: value.attributionModel,
                accountingMode: value.accountingMode,
                attributionWindow: value.attributionWindow,
                compareDateRange: value.compareDateRange,
                filterQuery: value.filterQuery,
                filters: value.filters,
                advancedSearch: value.advancedSearch,
                active: false,
                visible: true,
                index: chartState.metrics.length,
              },
            ];

            setChartState({ ...chartState, metrics: newTiles });
            setIsTileModalOpen(false);
          }}
        />
      )}
    </div>
  );
};
